import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { addAsyncCases } from 'shared/utils/redux';
import { Pagination } from 'model/Pagination';

import {
  fetchCurrentProject,
  fetchFocusAreas,
  fetchProjectKind,
  fetchProjectLiveSettings,
  fetchProjects,
  fetchQuestionsStats,
  fetchReportAvailabilities,
  fetchSentimentState,
} from './asyncActions';
import { ProjectState, STORE_NAME } from './initialState';

export const projectSlice = createSlice({
  name: STORE_NAME,
  initialState: ProjectState.INITIAL_DOMAIN,
  reducers: {
    clearProject(state) {
      state.current = ProjectState.INITIAL_DOMAIN.current as Draft<ProjectState.Domain['current']>;
    },
    clearProjectKindAndSettings(state) {
      state.kind = ProjectState.INITIAL_DOMAIN.kind as Draft<ProjectState.Domain['kind']>;
      state.liveSettings = ProjectState.INITIAL_DOMAIN.liveSettings as Draft<
        ProjectState.Domain['liveSettings']
      >;
    },
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      asyncActionCreator: fetchProjects,
      reducer: (state, action) => {
        state.list = action.payload as Draft<ProjectState.Domain['list']>;
      },
      initialState: ProjectState.INITIAL_DOMAIN.list.data,
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchCurrentProject,
      reducer: (state, action) => {
        state.current = action.payload as Draft<ProjectState.Domain['current']>;
      },
      initialState: ProjectState.INITIAL_DOMAIN.current.data,
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchQuestionsStats,
      reducer: (state, action) => {
        state.questionsStats = action.payload as ProjectState.Domain['questionsStats'];
      },
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchReportAvailabilities,
      reducer: (state, action) => {
        state.reportAvailabilities = action.payload as ProjectState.Domain['reportAvailabilities'];
      },
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchSentimentState,
      reducer: (state, action) => {
        state.sentiment =
          (action.payload as ProjectState.Domain['sentiment']) ||
          ProjectState.INITIAL_DOMAIN.sentiment;
      },
      initialState: ProjectState.INITIAL_DOMAIN.sentiment.data,
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchProjectLiveSettings,
      reducer: (state, action) => {
        state.liveSettings = action.payload;
      },
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchProjectKind,
      reducer: (state, action) => {
        state.kind = action.payload;
      },
    });
    addAsyncCases({
      builder,
      asyncActionCreator: fetchFocusAreas,
      reducer: (state, action) => {
        state.focusAreas = action.payload;
      },
    });
  },
});

export const { clearProject, clearProjectKindAndSettings, setPagination } = projectSlice.actions;
